import { z } from 'zod';
import { numberLocale } from '@/lib/language';
import type { LinkedAdsAccount } from '@/hooks/useLinkedAdsAccount';

/**
 * Kapitel 3 des Feed-Checks: der Vertrag zum jsonb `ads_feed_summaries.summary`
 * (Migration 20260910100000, Spec 3.1; Evidenzschwelle `schema_version 2`
 * additiv seit Spec 2026-09-23-kapitel3-evidenzschwelle-design.md, Abschnitt
 * D2/D4). Zod prueft beim Lesen; ein fremdes Payload liefert "keine Zahlen",
 * nie ein halbes Kapitel.
 *
 * `schema_version` traegt die Unterscheidung: v1 (Bestand) hat `leaks.zero_sellers`/
 * `leaks.drain_no_sale` flach, v2 (Evidenzschwelle) teilt beide in `above`/`below`
 * und traegt zusaetzlich `evidence`. Alle uebrigen Felder sind identisch --
 * `hasEvidenceSplit` ist die einzige Typwache dafuer.
 *
 * SYNC REQUIRED mit `supabase/functions/_shared/ads-feed-summary.ts`
 * (`AdsFeedSummaryJson`, `mergeFeedSummaryPartials`). Deno und Vite koennen kein
 * Modul teilen, das hier ist die andere Haelfte desselben Vertrags: der
 * Schreiber baut das Payload, dieses Schema nimmt es an oder ab.
 */
export const SEGMENT_ORDER = ['hero', 'mid', 'drain', 'zombie'] as const;
export type SegmentKey = (typeof SEGMENT_ORDER)[number];

const segmentSchema = z.object({
  key: z.enum(SEGMENT_ORDER),
  products: z.number(),
  cost_micros: z.number(),
  conversions_value: z.number(),
  conversions: z.number(),
  clicks: z.number(),
  roas: z.number().nullable(),
});

const topProductSchema = z.object({
  product_id: z.string(),
  title: z.string().nullable(),
  clicks: z.number(),
  cost_micros: z.number(),
});

const unoptimizedWithClicksSchema = z.object({ products: z.number(), clicks: z.number(), cost_micros: z.number() });
const disapprovedSchema = z.object({ products: z.number(), with_cost_7d: z.number(), cost_micros_7d: z.number() });

/**
 * Die Felder, die schema_version 1 UND 2 gemeinsam tragen (Spec D2). Ein
 * Objekt-Literal, kein `z.object`, damit es in beide `z.object({...})`-Aufrufe
 * gespreizt werden kann -- ein gemeinsamer Basis-`z.object().extend(...)`
 * bruechte hier nichts, weil `leaks` in v1 und v2 verschieden geformt ist und
 * v2 zusaetzlich `evidence` traegt.
 */
const baseSummaryFields = {
  products_total: z.number().int().nonnegative(),
  products_without_ads: z.number().int().nonnegative(),
  products_with_ads: z.number().int().nonnegative(),
  products_with_cost: z.number().int().nonnegative(),
  cost_micros: z.number(),
  conversions_value: z.number(),
  conversions: z.number(),
  clicks: z.number(),
  roas: z.number().nullable(),
  hero_revenue_share: z.number().nullable(),
  // Der Schreiber tippt das Feld als `string`, geschrieben werden aber nur
  // diese beiden Werte (`v_roas_source` in 20260910100000, Default-Parameter in
  // 20260910100200). Ein dritter Wert MUSS hier nachgezogen werden, sonst faellt
  // das Kapitel bei jedem Kunden auf "keine Zahlen" zurueck.
  account_roas_source: z.enum(['rollup', 'raw']),
  segments: z.array(segmentSchema),
};

export const AdsFeedSummaryV1Schema = z.object({
  schema_version: z.literal(1),
  ...baseSummaryFields,
  leaks: z.object({
    zero_sellers: z.object({
      products: z.number(),
      cost_micros: z.number(),
      top: z.array(topProductSchema),
    }),
    drain_no_sale: z.object({ products: z.number(), cost_micros: z.number() }),
    unoptimized_with_clicks: unoptimizedWithClicksSchema,
    disapproved: disapprovedSchema,
  }),
});
export type AdsFeedSummaryV1 = z.infer<typeof AdsFeedSummaryV1Schema>;

/**
 * Ein Klick-Split (Spec D2): `products`/`clicks`/`cost_micros` je Seite der
 * Evidenzschwelle. `above` traegt bei `zero_sellers` zusaetzlich die
 * Top-Liste, bei `drain_no_sale` nicht -- deshalb zwei Schemas statt eines
 * gemeinsamen `.extend()`.
 */
const clickSplitSchema = z.object({
  products: z.number(),
  clicks: z.number(),
  cost_micros: z.number(),
});

export const AdsFeedSummaryV2Schema = z.object({
  schema_version: z.literal(2),
  ...baseSummaryFields,
  evidence: z.object({
    min_clicks: z.number().int().min(1),
    conversion_rate: z.number().nullable(),
    basis_clicks: z.number(),
    basis_conversions: z.number(),
    // Pflichtfeld (Fix-Welle 1, Review-Fund I2): der Schreiber ist die eine
    // Quelle fuer "haelt die Schwelle wirklich das Versprechen 'kaum Zufall'"
    // (`evidenceIsReliable` in `supabase/functions/_shared/ads-feed-summary.ts`).
    // Ohne `reliable` faellt die ganze Zeile durch Zod -- besser ein "keine
    // Zahlen" als eine Anzeige, die "kaum Zufall" sagt, wo CR 0 war. Es
    // existiert noch keine v2-Zeile in Produktion, ein Pflichtfeld ist also
    // ohne Migrationskosten.
    reliable: z.boolean(),
  }),
  leaks: z.object({
    zero_sellers: z.object({
      products: z.number(),
      clicks: z.number(),
      cost_micros: z.number(),
      above: clickSplitSchema.extend({ top: z.array(topProductSchema) }),
      below: clickSplitSchema,
    }),
    drain_no_sale: z.object({
      products: z.number(),
      clicks: z.number(),
      cost_micros: z.number(),
      above: clickSplitSchema,
      below: clickSplitSchema,
    }),
    unoptimized_with_clicks: unoptimizedWithClicksSchema,
    disapproved: disapprovedSchema,
  }),
});
export type AdsFeedSummaryV2 = z.infer<typeof AdsFeedSummaryV2Schema>;

/**
 * Diskriminierte Union ueber `schema_version` (Spec D4). Ein fremder oder
 * unbekannter Versionswert (z. B. `3`) lehnt `safeParse` ab -- `parseAdsFeedSummary`
 * liefert dann `null`, nie ein halb gelesenes Kapitel.
 */
export const AdsFeedSummarySchema = z.discriminatedUnion('schema_version', [
  AdsFeedSummaryV1Schema,
  AdsFeedSummaryV2Schema,
]);
export type AdsFeedSummary = z.infer<typeof AdsFeedSummarySchema>;

/** Typwache: erst nach diesem Aufruf kennt TypeScript `summary.evidence` und die geteilte `above`/`below`-Form der Leck-Bloecke. */
export function hasEvidenceSplit(summary: AdsFeedSummary): summary is AdsFeedSummaryV2 {
  return summary.schema_version === 2;
}

export interface AdsFeedSummaryRow {
  feed_id: string;
  ads_account_id: string;
  window_start: string;
  window_end: string;
  data_through: string | null;
  currency: string | null;
  summary: unknown;
  computed_at: string;
}

export interface ParsedAdsFeedSummary {
  row: AdsFeedSummaryRow;
  summary: AdsFeedSummary;
}

export function parseAdsFeedSummary(row: AdsFeedSummaryRow): ParsedAdsFeedSummary | null {
  const result = AdsFeedSummarySchema.safeParse(row.summary);
  return result.success ? { row, summary: result.data } : null;
}

/**
 * A2-NEU-8: im Ads-Kapitel gewinnt die LIVE-Zahl der von Google abgelehnten
 * Produkte (`products.shopping_ads_disapproved_at`, `useFeedDisapprovals`) vor
 * der Zahl aus dem Cache. Beide zaehlen dasselbe, aber der Cache entsteht am
 * Ende des Ads-Pulls (06:31 bis 06:41 UTC) und der Ablehnungs-Abruf schreibt
 * erst um 06:45 UTC: der Cache zeigte deshalb immer den Stand von gestern, und
 * auf einer Seite standen zwei Zahlen fuer dieselbe Sache (443 oben, 438 hier).
 *
 * 0, negativ, NaN oder unbekannt laesst den Cache stehen: der Live-Hook liefert
 * `null` fuer "keine Ablehnungen" UND fuer "laedt noch" und kann die beiden
 * nicht unterscheiden. Die 7-Tage-Kosten gibt es nur im Cache, sie bleiben.
 */
export function withLiveDisapproved(
  parsed: ParsedAdsFeedSummary,
  live: number | null | undefined,
): ParsedAdsFeedSummary {
  const cached = parsed.summary.leaks.disapproved;
  if (typeof live !== 'number' || !Number.isFinite(live) || live <= 0 || live === cached.products) return parsed;
  const disapproved = { ...cached, products: live, with_cost_7d: Math.min(cached.with_cost_7d, live) };
  // Cast: der Spread ueber die v1/v2-Union verliert den Diskriminator
  // `schema_version`; geaendert wird ausschliesslich `leaks.disapproved`.
  const summary = { ...parsed.summary, leaks: { ...parsed.summary.leaks, disapproved } } as AdsFeedSummary;
  return { ...parsed, summary };
}

export const STALE_COMPUTED_HOURS = 48;
export const STALE_DATA_DAYS = 4;

/**
 * Veraltet, wenn die Rechnung aelter als 48 h ODER der juengste Ads-Tag dieses
 * Feeds aelter als 4 Tage ist. NULL data_through (Feed ohne jede Zeile) ist
 * KEIN Frische-Problem; sonst rutschte der datenlose Kunde nach dem ersten
 * Refresh sofort in die Warn-Fussnote (Spec 2, Zustandstabelle).
 */
export function isSummaryStale(row: AdsFeedSummaryRow, now: Date): boolean {
  const computedAgeH = (now.getTime() - new Date(row.computed_at).getTime()) / 3_600_000;
  if (computedAgeH > STALE_COMPUTED_HOURS) return true;
  if (row.data_through === null) return false;
  const dataAgeDays = (now.getTime() - new Date(`${row.data_through}T00:00:00Z`).getTime()) / 86_400_000;
  return dataAgeDays > STALE_DATA_DAYS;
}

export type AdsChapterState =
  | 'not_connected' | 'unlinked' | 'owner_managed' | 'no_numbers' | 'ready' | 'stale' | 'no_data';

/**
 * Ist das Ads-Konto mit diesem Merchant Center verknuepft? Fuer `no_numbers`,
 * `ready`, `stale` und `no_data` ja; `not_connected`, `unlinked` und
 * `owner_managed` (Mitglied, die Verknuepfung liegt beim Eigentuemer) nein.
 */
export function isAdsLinkedState(state: AdsChapterState): boolean {
  return state === 'no_numbers' || state === 'ready' || state === 'stale' || state === 'no_data';
}

export function deriveAdsChapterState(input: {
  hasAnyAdsAccount: boolean;
  linked: LinkedAdsAccount | null;
  parsed: ParsedAdsFeedSummary | null;
  now: Date;
  /** Betrachter ist Mitglied (nicht Eigentuemer) der MC; fehlt das Feld, gilt false. */
  viewerIsMember?: boolean;
}): AdsChapterState {
  // Ein Mitglied ohne eigene Verknuepfung hat hier nichts zu verbinden: die
  // Verknuepfung gehoert dem Eigentuemer, auch wenn am Grant ein Ads-Konto haengt.
  if (input.viewerIsMember && !input.linked) return 'owner_managed';
  if (!input.hasAnyAdsAccount && !input.linked) return 'not_connected';
  if (!input.linked) return 'unlinked';
  if (!input.parsed) return 'no_numbers';
  // EIN FEED OHNE PRODUKTE HAT NICHTS ZU ZEIGEN, auch kein `no_data`.
  //
  // `ads_feed_summary_scope` nimmt einen Feed ueber seinen zweiten Zweig auch
  // dann auf, wenn er KEINE Produkte mehr hat, aber noch Leistungszeilen
  // traegt -- nach einem Sync, der den Feed geleert hat, ist das der
  // Normalfall. Der Chunk-Pfad schreibt dafuer eine Zeile mit lauter Nullen.
  // `no_data` rendert daraus "Keines deiner 0 Produkte wurde in 28 Tagen
  // gezeigt", also einen Satz ueber Anzeigen fuer einen Feed, ueber den es
  // nichts zu sagen gibt. `no_numbers` sagt genau das.
  if (input.parsed.summary.products_total === 0) return 'no_numbers';
  if (input.parsed.summary.products_with_ads === 0) return 'no_data';
  return isSummaryStale(input.parsed.row, input.now) ? 'stale' : 'ready';
}

export function shareWithoutAds(summary: Pick<AdsFeedSummary, 'products_total' | 'products_without_ads'>): number {
  if (summary.products_total === 0) return 0;
  return Math.round((summary.products_without_ads / summary.products_total) * 100);
}

/** Micros zu Waehrung ohne Nachkommastellen; ohne Waehrung nur die Zahl (keine geratene Waehrung). */
export function formatMicros(micros: number, currency: string | null, language: string | undefined): string {
  const value = Math.round(micros / 1e6);
  const locale = numberLocale(language);
  if (!currency) return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)} ${currency}`;
  }
}
